import ApiService from "./ApiService";
import {
  LOG_SEVERITIES,
  MESSAGES_TYPES,
  REDIRECT_TYPES,
  URL_PARAMS,
  WHATSAPP_CONTACT_URL,
  MOBILE_HOSTS,
  PROVIDERS_TYPE,
  USER_TYPES,
  WIZE_EXT_APPS,
} from "../assets/appConstants";

/**
 * @param {string} param Values from appConstants::URL_PARAMS
 * @param {boolean} toLowerCase
 * @returns {?string}
 */
export const getUrlParam = (param, toLowerCase = false) => {
  const params = new URLSearchParams(window.location.search);
  return (toLowerCase) ? params.get(param)?.toLowerCase() : params.get(param);
}

export const urlParams = () => {
  const paramObj = {};

  const params = new URLSearchParams(window.location.search);

  for (let value of params.keys()) {
    paramObj[value] = params.get(value);
  }

  return paramObj;
}

export const postMessage = (message, value = {}) => {
  ApiService.logIt("posting to parent: " + message, LOG_SEVERITIES.DEBUG, value)
  window.parent.postMessage({ message, value }, "*");
}

export const redirect = (redirectType, params = {}, options = {}) => {
  const { preserveOtl = false } = options;

  let route = '/'
  switch (redirectType) {
    case REDIRECT_TYPES.HOME:
      postMessage(MESSAGES_TYPES.BACK_HOME)
      break;
    case REDIRECT_TYPES.FORGOT_PASSWORD:
      params.forgot = '1'
      break;
    case REDIRECT_TYPES.RESET_PASSWORD:
      route = '/reset';
      break;
    case REDIRECT_TYPES.ONBOARDING:
      route = '/onboarding';
      break;
    case REDIRECT_TYPES.IOS_CLINICIAN:
      params.host = MOBILE_HOSTS.IOS;
      params[URL_PARAMS.APPTYPE] = USER_TYPES.CLINICIAN;
      break;
    default:
      ApiService.logIt("Invalid redirect type", LOG_SEVERITIES.WARNING)
      break;
  }

  let paramsString = '?'

  for (const property in params) {
    const shouldExclude =
      (property === URL_PARAMS.OTL &&
        !(redirectType === REDIRECT_TYPES.ONBOARDING && preserveOtl)) ||
      property === URL_PARAMS.REGISTRATION_CODE ||
      (property === URL_PARAMS.RESET_CODE && redirectType !== REDIRECT_TYPES.RESET_PASSWORD);

    if (!shouldExclude){
      paramsString +=`${property}=${params[property]}&`
    }
  }
  ApiService.logIt("Redirecting to: " + route + paramsString, LOG_SEVERITIES.DEBUG)
  window.location.replace(route + paramsString);

}

export const normalizeAccountTypeHint = (hint) => {
  if (!hint) return "";
  const normalized = String(hint).toLowerCase();
  if (
    normalized === USER_TYPES.CLINICIAN ||
    normalized === WIZE_EXT_APPS.CLINICIAN ||
    normalized === "clinician"
  ) {
    return USER_TYPES.THERAPIST;
  }
  if (normalized === WIZE_EXT_APPS.PATIENT || normalized === USER_TYPES.PATIENT) {
    return USER_TYPES.PATIENT;
  }
  return normalized;
};

/**
 * Resolve account/app type from URL query params.
 * Supports `apptype=clinician` and invite links like `wize_ext_additional_apps=clinician_app`.
 */
export const resolveAppTypeHintFromUrlParams = (params = {}) => {
  const apptype = params[URL_PARAMS.APPTYPE] || params.apptype;
  if (apptype) {
    return normalizeAccountTypeHint(apptype);
  }

  const extApps =
    params[URL_PARAMS.WIZE_EXT_ADDITIONAL_APPS] || params.wize_ext_additional_apps;
  if (!extApps) {
    return "";
  }

  const apps = String(extApps)
    .toLowerCase()
    .split(",")
    .map((value) => value.trim());

  if (apps.some((app) => app === WIZE_EXT_APPS.CLINICIAN || app === USER_TYPES.CLINICIAN)) {
    return USER_TYPES.THERAPIST;
  }
  if (apps.some((app) => app === WIZE_EXT_APPS.PATIENT || app === USER_TYPES.PATIENT)) {
    return USER_TYPES.PATIENT;
  }

  return "";
};

export const buildOnboardingRedirectParams = (params = {}, extra = {}) => {
  const redirectParams = { ...extra };
  const extApps =
    params[URL_PARAMS.WIZE_EXT_ADDITIONAL_APPS] || params.wize_ext_additional_apps;
  if (extApps) {
    redirectParams[URL_PARAMS.WIZE_EXT_ADDITIONAL_APPS] = extApps;
  }
  const appTypeHint = resolveAppTypeHintFromUrlParams(params);
  if (appTypeHint && !extApps) {
    redirectParams[URL_PARAMS.APPTYPE] =
      appTypeHint === USER_TYPES.THERAPIST ? USER_TYPES.CLINICIAN : appTypeHint;
  }
  return redirectParams;
};

/**
 * Get and normalize the app type from URL parameters
 * Handles the mismatch between URL param 'clinician' and account type 'therapist'
 * Defaults to 'patient' if no apptype parameter is found
 * @returns {string} 
 */
export const getAppType = () => {
  const appTypeFromUrl = resolveAppTypeHintFromUrlParams(urlParams());

  if (!appTypeFromUrl) {
    return isClientMobile() ? USER_TYPES.PATIENT : null;
  }

  return appTypeFromUrl;
}

/**
 * Check if the current client is a mobile device
 * @returns {boolean} - Returns true if the client is mobile, false otherwise
 */
export const isClientMobile = () => {
  return Object.values(MOBILE_HOSTS).indexOf(ApiService.getAppHost()) > -1;
}

/**
 * Check if this is an iOS device using Google or SSO login (workaround case)
 * This workaround is needed because iOS handles Google and SSO authentication natively
 * 
 * @param {Object} loginData - The login response data containing providerType
 * @returns {boolean} - Returns true if this is an iOS Google/SSO case that should skip validation
 */
export const isIosGoogleOrSSOWorkaround = (loginData) => {
  const host = getUrlParam(URL_PARAMS.HOST, true);
  return host === MOBILE_HOSTS.IOS && 
    (loginData.providerType === PROVIDERS_TYPE.GOOGLE || loginData.providerType === PROVIDERS_TYPE.SAML);
}

/**
 * Checks if the user type matches the application type
 * 
 * @param {Object} loginData - The login response data containing accountType
 * @returns {boolean} - Returns true if validation passes, false if there's a mismatch
 */
export const validateUserTypeMatchesApp = (loginData) => {
  const appType = getAppType();
  const accountType = loginData.accountType?.toLowerCase();

  // Skip validation only for:
  // 1. If account type is not defined  
  // 2. If using web app (not mobile) and no app type specified
  // 3. If using iOS with Google sign-in or SSO (native authentication)
  if (!accountType || (!isClientMobile() && !appType) || isIosGoogleOrSSOWorkaround(loginData)) {
    return true;
  }
  
  // For mobile apps without apptype parameter, assume it's a patient app
  const effectiveAppType = appType || USER_TYPES.PATIENT;

  // Therapist (API) and clinician (app URL) refer to the same user type.
  const normalizeForAppMatch = (type) => {
    const normalized = type?.toLowerCase();
    if (normalized === USER_TYPES.THERAPIST) {
      return USER_TYPES.CLINICIAN;
    }
    return normalized;
  };

  return (
    normalizeForAppMatch(effectiveAppType) === normalizeForAppMatch(accountType)
  );
};
